import React from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';

/**
 * Loading and error states that sit inside a page or card, so the page header and navigation stay
 * on screen. Use these instead of a full-height spinner or a "not found" message for a failed load.
 */
export const LoadingState = ({ label = 'Loading', className = 'p-4' }) => (
  <div className={`d-flex align-items-center justify-content-center gap-2 text-muted ${className}`} role="status">
    <Spinner animation="border" size="sm" aria-hidden="true" />
    <span>{label}...</span>
  </div>
);

/** One sentence on what failed, plus a Try again button when the caller can retry. */
export const ErrorState = ({ message, onRetry, className = '', inCard = false }) => {
  const content = (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
      <span>{message}</span>
      {onRetry && (
        <Button variant="outline-secondary" size="sm" onClick={onRetry}>Try again</Button>
      )}
    </div>
  );
  if (inCard) {
    return <div className={`p-4 text-muted ${className}`} role="alert">{content}</div>;
  }
  return <Alert variant="danger" className={className}>{content}</Alert>;
};

/**
 * True when the server said the record does not exist, so "not found" is only said when it is true.
 * The API's exception handler answers "Company not found with id ..." as a 400, so that counts too.
 */
export const isNotFound = (err) => {
  const status = err?.response?.status;
  if (status === 404) return true;
  const message = err?.response?.data?.message || err?.response?.data?.error || '';
  return status === 400 && /\bnot found\b/i.test(message);
};

/** The server's own message for a failed request, when it sent one. */
export const serverMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;
